import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeading from './SectionHeading';
import { getLocaleList } from '../utils/localeList';
import useReveal from '../hooks/useReveal';
import ProductCard from './ProductCard';
import PartnersSlider from './PartnersSlider';

/**
 * Section 06 — "evidence before adjectives". The three claims come straight from
 * the positioning; everything under them exists to back the claims with numbers
 * and logos rather than more adjectives.
 *
 * The two products are that evidence, which is why they live inside this section
 * rather than in one of their own: what makes us worth integrating is that we run
 * these in production. Each card carries its own live Dune figures, so the
 * visitor sees how each product is actually doing instead of one blended total.
 * The `#products` anchor sits on the card grid so the nav link still lands on
 * them.
 *
 * The section reads as one argument, in this order: the claim, how fast it
 * happens, the numbers, and a client saying it in their own words.
 *
 * - The speed pair answers the question the claims provoke ("how long does this
 *   take me?") before the visitor has to ask it.
 * - The Dune figures used to sit bare, which read as product statistics. The
 *   line above them says what they actually are — not a case study, the thing
 *   running right now — which is the one piece of evidence a competitor with a
 *   slide deck cannot fake.
 * - The testimonial closes it with a human voice. It is quoted in English in
 *   every locale on purpose: they are a real person's published words, and
 *   translating them would attribute sentences to him he never said.
 * - The team block (`AboutUs`) is not rendered for now. It used to close the
 *   section; putting it back is one import and one `<AboutUs />` after the
 *   partners. See docs/modules/team.md.
 */
const WhyPaltaLabs = () => {
  const { t } = useTranslation();
  const reveal = useReveal();
  const speed = getLocaleList(t, 'why.speed');

  return (
    <div id="why-paltalabs" className="mainCont relative z-30 pt-5 mt-[100px] lg4:mt-[150px]" ref={reveal} data-reveal>
      <SectionHeading eyebrow={t('why.eyebrow')} title={t('why.title')} />

      <p className="font-grotesk text-[24px] sm3:text-[32px] font-bold text-white leading-[125%] max-w-[900px]">
        {t('why.proof')}
      </p>

      <div className="grid grid-cols-1 md2:grid-cols-2 gap-6 mt-8">
        <div className="border-l-2 border-main pl-5">
          <p className="text-main-gray">{t('why.specialization')}</p>
        </div>
        <div className="border-l-2 border-main pl-5">
          <p className="text-main-gray">{t('why.stellar')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md2:grid-cols-2 gap-6 mt-10">
        {speed.map((stat) => (
          <div
            key={stat.value}
            className="landingCard p-6"
          >
            <div className="font-grotesk font-bold text-main text-[40px] sm3:text-[48px] leading-none">
              {stat.value}
            </div>
            <p className="text-main-gray text-sm mt-3">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-14">
        <p className="font-grotesk text-[22px] sm3:text-[28px] font-bold text-white leading-[125%] max-w-[900px]">
          {t('why.evidenceTitle')}
        </p>
        <p className="text-main-gray text-sm mt-3 max-w-[720px]">{t('why.evidenceNote')}</p>
      </div>

      <div id="products" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <ProductCard
          title="Soroswap.finance"
          logo="https://links.paltalabs.io/soroswap-full.svg"
          descriptionKey="products.soroswap.description"
          metricsKey="products.soroswap.metrics"
          protocolKey="soroswap"
          socialLinks={[
            { platform: 'X', url: 'https://x.com/SoroswapFinance' },
            { platform: 'LinkedIn', url: 'https://www.linkedin.com/company/soroswap' },
            { platform: 'Discord', url: 'https://discord.gg/A2rQ3cm9XG' },
            { platform: 'GitHub', url: 'https://github.com/soroswap' },
            { platform: 'Dune', url: 'https://dune.com/paltalabs/soroswap' },
            { platform: 'Medium', url: 'https://medium.com/soroswap' }
          ]}
          projectUrl="https://soroswap.finance"
          docsUrl="https://docs.soroswap.finance/"
        />

        <ProductCard
          title="DeFindex"
          logo="https://links.paltalabs.io/defindex-full.svg"
          descriptionKey="products.defindex.description"
          metricsKey="products.defindex.metrics"
          protocolKey="defindex"
          socialLinks={[
            { platform: 'X', url: 'https://x.com/defindex_' },
            { platform: 'LinkedIn', url: 'https://www.linkedin.com/company/defindex/' },
            { platform: 'Discord', url: 'https://discord.gg/CUC26qUTw7' },
            { platform: 'GitHub', url: 'https://github.com/paltalabs/defindex' },
            { platform: 'Dune', url: 'https://dune.com/paltalabs/defindex' }
          ]}
          projectUrl="https://www.defindex.io"
          docsUrl="https://docs.defindex.io/"
        />
      </div>

      <figure className="landingCardWide p-6 sm3:p-8 mt-10">
        <blockquote className="font-grotesk text-[20px] sm3:text-[26px] text-white leading-[140%]">
          “{t('why.testimonialQuote')}”
        </blockquote>
        <figcaption className="mt-5 border-t border-white/10 pt-4">
          <span className="font-grotesk font-bold text-main">{t('why.testimonialName')}</span>
          <span className="text-main-gray text-sm ml-2">{t('why.testimonialRole')}</span>
        </figcaption>
      </figure>

      <div className="mt-16">
        <h3 className="text-center text-main-gray text-sm uppercase tracking-[2px] mb-8">
          {t('why.partnersTitle')}
        </h3>
        <PartnersSlider />
      </div>

    </div>
  );
};

export default WhyPaltaLabs;
